import { useAppLocation } from "@/lib/app-navigation";
import {
  getBlogById,
  getBlogBySlug,
  getBlogs,
  getPublishedBlogFromListByKey,
  fetchBloggerPublicSocial,
  normalizeFaqs,
  normalizeSocialLinks,
  pickFaqSourceForBlog,
  transformBlogForFrontend,
} from "@/platforms/customer/services/blogService";
import { useRouter } from "next/navigation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { rewriteHtmlImgSrcsForApiOrigin } from "@/utils/mediaUrlFromApi";
import { stripInlineFontFamilies } from "@/utils/stripInlineFontFamilies";
import { useBlogDetailUtils } from "./useBlogDetailUtils";
import { useEnhanceProductCardsHtmlForBlogDetails } from "./useEnhanceProductCardsHtmlForBlogDetails";
import { useBlogDetailSocials } from "./useBlogDetailSocials";
import { appOrigin } from "@/config/env";
import { blogCanonicalPath } from "@/platforms/customer/utils/blogSeo";

export const useBlogDetail = ({ initialBlog = null, pathKey = "" } = {}) => {
  const {
    blogKeyFromPathname,
    ensureHttpHref,
    isJunkOneLetterHost,
    socialLinksPayloadLooksEmpty,
    faqsPayloadLooksEmpty,
    enrichRawBlogFromPublicList,
    socialLinksObjectEmpty,
    extractWriterIdForSocialApi,
    patchDisplayBlogFromListRow,
    normalizeHtmlLinks,
    getInitials,
    blogSortTimestamp,
    truncateWithEllipsis,
    buildTocAndHtml,
  } = useBlogDetailUtils();
  const { enhanceProductCardsHtml } =
    useEnhanceProductCardsHtmlForBlogDetails();
  const location = useAppLocation();
  const id = useMemo(() => {
    const fromProp = pathKey != null ? String(pathKey).trim() : "";
    if (fromProp) return fromProp;
    return blogKeyFromPathname(location.pathname);
  }, [pathKey, location.pathname, blogKeyFromPathname]);
  const router = useRouter();
  const [loading, setLoading] = useState(() => !initialBlog);
  const [err, setErr] = useState("");
  const [blog, setBlog] = useState(() => initialBlog || null);
  const [rawBlogForSocial, setRawBlogForSocial] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const [shareDisplayUrl, setShareDisplayUrl] = useState("");
  const shareWrapRef = useRef(null);
  const shareMobileWrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!initialBlog) {
          setLoading(true);
          setBlog(null);
        }
        setErr("");

        let b = null;
        let rawBlog = null;
        const blogKey = id != null ? String(id).trim() : "";

        // Phase 1 Minimal Fix: Do not re-fetch main blog if initialBlog already has data
        if (initialBlog && initialBlog.content) {
          b = initialBlog;
        } else {
          setRawBlogForSocial(null);
          let fetchKey = blogKey;
          try {
            if (fetchKey && (fetchKey.includes("%3A") || fetchKey.includes("%3a")))
              fetchKey = decodeURIComponent(fetchKey);
          } catch {}
          const isPublicId = fetchKey.includes(":");
          const isNumericId = /^\d+$/.test(fetchKey);

          let data = isPublicId ? await getBlogById(fetchKey) : null;
          if (!data) {
            data = isNumericId ? await getBlogById(fetchKey) : await getBlogBySlug(fetchKey);
          }
          if (!data) {
            const fromList = await getPublishedBlogFromListByKey(fetchKey);
            if (fromList) {
              let blogPayload = { ...fromList };
              const needsFull = !String(blogPayload.content || blogPayload.body || "").trim();
              if (needsFull) {
                const sl = blogPayload.slug != null ? String(blogPayload.slug).trim() : "";
                let full = sl ? await getBlogBySlug(sl) : null;
                if (!full && blogPayload.id) full = await getBlogById(String(blogPayload.id));
                if (full) {
                  const ra = full.blog != null ? full.blog : full;
                  const content = ra.content || ra.body || blogPayload.content;
                  blogPayload = { ...blogPayload, ...ra, content };
                } else if (String(blogPayload.excerpt || "").trim()) {
                  blogPayload = {
                    ...blogPayload,
                    content: `<p>${String(blogPayload.excerpt).trim().replace(/</g, "&lt;")}</p>`,
                  };
                }
              }
              data = { blog: blogPayload };
            }
          }
          rawBlog = data?.blog != null ? data.blog : data;
        }

        // Fetch related blogs list
        const all = await getBlogs({ summary: 1 });

        if (!b && rawBlog) {
          rawBlog = enrichRawBlogFromPublicList(rawBlog, all?.blogs || [], blogKey);
          if (rawBlog && typeof rawBlog === "object") {
            setRawBlogForSocial({ ...rawBlog });
          }
          b = transformBlogForFrontend(rawBlog);
        }

        if (!alive) return;
        if (!b) {
          if (!initialBlog) {
            setBlog(null);
            setErr("Blog not found.");
          }
          return;
        }

        if (b && (all?.blogs || []).length) {
          b = patchDisplayBlogFromListRow(b, all.blogs, blogKey);
        }

        // Handle missing social links
        if (b && rawBlog && typeof rawBlog === "object" && socialLinksObjectEmpty(b.socialLinks)) {
          const n = normalizeSocialLinks(rawBlog.social_links || rawBlog.writer_social_links || rawBlog.socialLinks);
          if (!socialLinksObjectEmpty(n)) {
            b = { ...b, socialLinks: n, writerId: b.writerId || (rawBlog.writer_id != null ? String(rawBlog.writer_id).trim() : null) };
          }
        }
        const wIdForSocial = extractWriterIdForSocialApi(rawBlog || initialBlog, b);
        if (b && wIdForSocial && socialLinksObjectEmpty(b.socialLinks)) {
          const socialPayload = await fetchBloggerPublicSocial(wIdForSocial);
          const rawSl = socialPayload?.social ?? socialPayload?.social_links;
          if (rawSl != null) {
            const t = transformBlogForFrontend({ social_links: rawSl, author: b.author });
            if (t && !socialLinksObjectEmpty(t.socialLinks)) {
              b = { ...b, socialLinks: t.socialLinks, writerId: b.writerId || wIdForSocial };
              setRawBlogForSocial((prev) => prev && typeof prev === "object" ? { ...prev, social_links: rawSl } : { social_links: rawSl });
            }
          }
        }

        if (!alive) return;
        setBlog(b);

        // Related blogs processing
        let allTransformed = (all?.blogs || []).map(transformBlogForFrontend).filter(Boolean);
        const related = allTransformed.filter((x) => x.id !== b.id && x.category && x.category === b.category).slice(0, 6);
        setRelatedBlogs(related);

        const latest = allTransformed.filter((x) => x.id !== b.id).slice().sort((a, bb) => blogSortTimestamp(bb) - blogSortTimestamp(a)).slice(0, 10);
        setLatestBlogs(latest);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load blog.");
        setBlog(null);
        setRelatedBlogs([]);
        setLatestBlogs([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, router, initialBlog]);

  const [canonicalUrl, setCanonicalUrl] = useState("");

  useEffect(() => {
    try {
      const origin =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : String(appOrigin() || "").replace(/\/+$/, "");

      if (!origin) {
        setCanonicalUrl("");
        return;
      }
      const path = blogCanonicalPath(blog, id);
      setCanonicalUrl(`${String(origin).replace(/\/+$/, "")}${path}`);
    } catch {
      setCanonicalUrl("");
    }
  }, [blog?.slug, blog?.id, id, blog]);

  const renderedHtml = useMemo(() => {
    if (!blog?.content) return "";
    const withImages = rewriteHtmlImgSrcsForApiOrigin(
      stripInlineFontFamilies(blog.content),
    );
    const normalized = enhanceProductCardsHtml(normalizeHtmlLinks(withImages));
    // Remove hidden / control characters that can cause "broken" looking text on mobile.
    return String(normalized || "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width
      .replace(/[\u202A-\u202E\u2066-\u2069]/g, "") // bidi isolates/overrides
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, "") // control chars
      .replace(/\uFFFD/g, ""); // replacement character
  }, [blog?.content]);

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const { toc, htmlWithIds } = useMemo(() => {
    if (!isMounted) return { toc: [], htmlWithIds: renderedHtml || "" };
    return buildTocAndHtml(renderedHtml);
  }, [renderedHtml, isMounted]);

  const { socialEntries } = useBlogDetailSocials(
    ensureHttpHref,
    isJunkOneLetterHost,
    blog,
    rawBlogForSocial,
  );

  const faqs = useMemo(() => {
    const fromBlog = normalizeFaqs(pickFaqSourceForBlog(blog));
    if (fromBlog.length) return fromBlog;
    if (rawBlogForSocial && typeof rawBlogForSocial === "object") {
      const fromRaw = normalizeFaqs(pickFaqSourceForBlog(rawBlogForSocial));
      if (fromRaw.length) return fromRaw;
    }
    return [];
  }, [blog, blog?.faqs, blog?.faq, rawBlogForSocial]);

  const getShareableUrl = () =>
    canonicalUrl ||
    (typeof window !== "undefined" ? window.location.href : "");

  useEffect(() => {
    if (!isShareOpen) return;
    setShareDisplayUrl(
      canonicalUrl ||
        (typeof window !== "undefined" ? window.location.href : ""),
    );
  }, [isShareOpen, canonicalUrl]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (!isShareOpen) return;
      const w = shareWrapRef.current;
      const wm = shareMobileWrapRef.current;
      const t = e.target;
      if (w?.contains(t)) return;
      if (wm?.contains(t)) return;
      setIsShareOpen(false);
      setCopyMessage("");
      setShareDisplayUrl("");
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isShareOpen]);

  /** Per-icon share URL (same count as share modal icons). */
  const buildShareOpenUrl = (channel) => {
    const url = getShareableUrl();
    const enc = encodeURIComponent(url);
    if (channel === "facebook")
      return `https://www.facebook.com/sharer/sharer.php?u=${enc}`;
    if (channel === "twitter")
      return `https://twitter.com/intent/tweet?url=${enc}`;
    if (channel === "linkedin")
      return `https://www.linkedin.com/sharing/share-offsite/?url=${enc}`;
    if (channel === "whatsapp") return `https://wa.me/?text=${enc}`;
    // Instagram has no web sharer — open app site; copy article URL instead.
    if (channel === "instagram") return "https://www.instagram.com/";
    return url;
  };

  /** Clipboard value for that icon (Instagram → article URL; others → their share URL). */
  const getChannelCopyUrl = (channel) => {
    if (channel === "instagram") return getShareableUrl();
    return buildShareOpenUrl(channel);
  };

  const handleCopyUrl = async () => {
    const toCopy = shareDisplayUrl || getShareableUrl();
    try {
      await navigator.clipboard.writeText(toCopy);
      setCopyMessage("copied!");
      setTimeout(() => setCopyMessage(""), 2000);
    } catch (_) {
      setCopyMessage("copy failed");
      setTimeout(() => setCopyMessage(""), 2000);
    }
  };

  const handleShareChannel = async (channel) => {
    const openUrl = buildShareOpenUrl(channel);
    const copyUrl = getChannelCopyUrl(channel);
    setShareDisplayUrl(copyUrl);
    try {
      window.open(openUrl, "_blank", "noopener,noreferrer");
    } catch (_) {
      // ignore
    }
    try {
      await navigator.clipboard.writeText(copyUrl);
      setCopyMessage("Link copied!");
      setTimeout(() => setCopyMessage(""), 2500);
    } catch (_) {
      setCopyMessage("Opened share — use Copy for link");
      setTimeout(() => setCopyMessage(""), 2500);
    }
  };

  const handleBackTwoSteps = () => {
    router.push(-4);
  };

  const scrollToTocId = (tocId) => {
    if (!tocId) return;
    // This page renders mobile + desktop DOM; headings (ids) can exist twice.
    // Prefer the currently visible element.
    const esc =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(String(tocId))
        : String(tocId).replace(/["\\]/g, "\\$&");
    const candidates = Array.from(document.querySelectorAll(`[id="${esc}"]`));
    const el =
      candidates.find((c) => c && c.offsetParent !== null) ||
      candidates[0] ||
      document.getElementById(tocId);
    if (!el) return;
    const headerOffset = 96; // sticky header space
    const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  return {
    getInitials,
    truncateWithEllipsis,
    router,
    loading,
    err,
    blog,
    relatedBlogs,
    latestBlogs,
    isShareOpen,
    setIsShareOpen,
    copyMessage,
    setCopyMessage,
    shareDisplayUrl,
    shareWrapRef,
    shareMobileWrapRef,
    canonicalUrl,
    renderedHtml,
    toc,
    htmlWithIds,
    socialEntries,
    faqs,
    handleCopyUrl,
    handleShareChannel,
    handleBackTwoSteps,
    scrollToTocId,
  };
};
